/**
 * ARIA attribute constants for SPDS components.
 *
 * These constants provide a consistent vocabulary for accessibility attributes
 * across the design system.
 */

// ARIA Role values (common widget and document roles)
export const ROLE_ALERT = 'alert';
export const ROLE_ALERTDIALOG = 'alertdialog';
export const ROLE_BUTTON = 'button';
export const ROLE_CHECKBOX = 'checkbox';
export const ROLE_COMBOBOX = 'combobox';
export const ROLE_DIALOG = 'dialog';
export const ROLE_GRID = 'grid';
export const ROLE_GRIDCELL = 'gridcell';
export const ROLE_GROUP = 'group';
export const ROLE_IMG = 'img';
export const ROLE_LINK = 'link';
export const ROLE_LIST = 'list';
export const ROLE_LISTBOX = 'listbox';
export const ROLE_LISTITEM = 'listitem';
export const ROLE_MENU = 'menu';
export const ROLE_MENUBAR = 'menubar';
export const ROLE_MENUITEM = 'menuitem';
export const ROLE_MENUITEMCHECKBOX = 'menuitemcheckbox';
export const ROLE_MENUITEMRADIO = 'menuitemradio';
export const ROLE_NAVIGATION = 'navigation';
export const ROLE_OPTION = 'option';
export const ROLE_PRESENTATION = 'presentation';
export const ROLE_PROGRESSBAR = 'progressbar';
export const ROLE_RADIO = 'radio';
export const ROLE_RADIOGROUP = 'radiogroup';
export const ROLE_REGION = 'region';
export const ROLE_SEARCH = 'search';
export const ROLE_SEARCHBOX = 'searchbox';
export const ROLE_SEPARATOR = 'separator';
export const ROLE_SLIDER = 'slider';
export const ROLE_SPINBUTTON = 'spinbutton';
export const ROLE_STATUS = 'status';
export const ROLE_SWITCH = 'switch';
export const ROLE_TAB = 'tab';
export const ROLE_TABLIST = 'tablist';
export const ROLE_TABPANEL = 'tabpanel';
export const ROLE_TEXTBOX = 'textbox';
export const ROLE_TIMER = 'timer';
export const ROLE_TOOLBAR = 'toolbar';
export const ROLE_TOOLTIP = 'tooltip';
export const ROLE_TREE = 'tree';
export const ROLE_TREEITEM = 'treeitem';

// aria-live values
export const LIVE_OFF = 'off';
export const LIVE_POLITE = 'polite';
export const LIVE_ASSERTIVE = 'assertive';

// aria-haspopup values
export const HASPOPUP_FALSE = 'false';
export const HASPOPUP_TRUE = 'true';
export const HASPOPUP_MENU = 'menu';
export const HASPOPUP_LISTBOX = 'listbox';
export const HASPOPUP_TREE = 'tree';
export const HASPOPUP_GRID = 'grid';
export const HASPOPUP_DIALOG = 'dialog';

// aria-current values
export const CURRENT_FALSE = 'false';
export const CURRENT_TRUE = 'true';
export const CURRENT_PAGE = 'page';
export const CURRENT_STEP = 'step';
export const CURRENT_LOCATION = 'location';
export const CURRENT_DATE = 'date';
export const CURRENT_TIME = 'time';

// aria-autocomplete values
export const AUTOCOMPLETE_NONE = 'none';
export const AUTOCOMPLETE_INLINE = 'inline';
export const AUTOCOMPLETE_LIST = 'list';
export const AUTOCOMPLETE_BOTH = 'both';

// aria-orientation values
export const ORIENTATION_HORIZONTAL = 'horizontal';
export const ORIENTATION_VERTICAL = 'vertical';

// aria-sort values
export const SORT_NONE = 'none';
export const SORT_ASCENDING = 'ascending';
export const SORT_DESCENDING = 'descending';
export const SORT_OTHER = 'other';

// Pre-composed options arrays
export const ARIA_ROLE_OPTIONS = Object.freeze([
  ROLE_ALERT,
  ROLE_ALERTDIALOG,
  ROLE_BUTTON,
  ROLE_CHECKBOX,
  ROLE_COMBOBOX,
  ROLE_DIALOG,
  ROLE_GRID,
  ROLE_GRIDCELL,
  ROLE_GROUP,
  ROLE_IMG,
  ROLE_LINK,
  ROLE_LIST,
  ROLE_LISTBOX,
  ROLE_LISTITEM,
  ROLE_MENU,
  ROLE_MENUBAR,
  ROLE_MENUITEM,
  ROLE_MENUITEMCHECKBOX,
  ROLE_MENUITEMRADIO,
  ROLE_NAVIGATION,
  ROLE_OPTION,
  ROLE_PRESENTATION,
  ROLE_PROGRESSBAR,
  ROLE_RADIO,
  ROLE_RADIOGROUP,
  ROLE_REGION,
  ROLE_SEARCH,
  ROLE_SEARCHBOX,
  ROLE_SEPARATOR,
  ROLE_SLIDER,
  ROLE_SPINBUTTON,
  ROLE_STATUS,
  ROLE_SWITCH,
  ROLE_TAB,
  ROLE_TABLIST,
  ROLE_TABPANEL,
  ROLE_TEXTBOX,
  ROLE_TIMER,
  ROLE_TOOLBAR,
  ROLE_TOOLTIP,
  ROLE_TREE,
  ROLE_TREEITEM,
]);

export const ARIA_LIVE_OPTIONS = Object.freeze([LIVE_OFF, LIVE_POLITE, LIVE_ASSERTIVE]);

export const ARIA_HASPOPUP_OPTIONS = Object.freeze([
  HASPOPUP_FALSE,
  HASPOPUP_TRUE,
  HASPOPUP_MENU,
  HASPOPUP_LISTBOX,
  HASPOPUP_TREE,
  HASPOPUP_GRID,
  HASPOPUP_DIALOG,
]);

export const ARIA_CURRENT_OPTIONS = Object.freeze([
  CURRENT_FALSE,
  CURRENT_TRUE,
  CURRENT_PAGE,
  CURRENT_STEP,
  CURRENT_LOCATION,
  CURRENT_DATE,
  CURRENT_TIME,
]);

export const ARIA_AUTOCOMPLETE_OPTIONS = Object.freeze([
  AUTOCOMPLETE_NONE,
  AUTOCOMPLETE_INLINE,
  AUTOCOMPLETE_LIST,
  AUTOCOMPLETE_BOTH,
]);

export const ARIA_ORIENTATION_OPTIONS = Object.freeze([
  ORIENTATION_HORIZONTAL,
  ORIENTATION_VERTICAL,
]);

export const ARIA_SORT_OPTIONS = Object.freeze([
  SORT_NONE,
  SORT_ASCENDING,
  SORT_DESCENDING,
  SORT_OTHER,
]);

export default {
  // Role values
  ROLE_ALERT,
  ROLE_ALERTDIALOG,
  ROLE_BUTTON,
  ROLE_CHECKBOX,
  ROLE_COMBOBOX,
  ROLE_DIALOG,
  ROLE_GRID,
  ROLE_GRIDCELL,
  ROLE_GROUP,
  ROLE_IMG,
  ROLE_LINK,
  ROLE_LIST,
  ROLE_LISTBOX,
  ROLE_LISTITEM,
  ROLE_MENU,
  ROLE_MENUBAR,
  ROLE_MENUITEM,
  ROLE_MENUITEMCHECKBOX,
  ROLE_MENUITEMRADIO,
  ROLE_NAVIGATION,
  ROLE_OPTION,
  ROLE_PRESENTATION,
  ROLE_PROGRESSBAR,
  ROLE_RADIO,
  ROLE_RADIOGROUP,
  ROLE_REGION,
  ROLE_SEARCH,
  ROLE_SEARCHBOX,
  ROLE_SEPARATOR,
  ROLE_SLIDER,
  ROLE_SPINBUTTON,
  ROLE_STATUS,
  ROLE_SWITCH,
  ROLE_TAB,
  ROLE_TABLIST,
  ROLE_TABPANEL,
  ROLE_TEXTBOX,
  ROLE_TIMER,
  ROLE_TOOLBAR,
  ROLE_TOOLTIP,
  ROLE_TREE,
  ROLE_TREEITEM,
  // Live values
  LIVE_ASSERTIVE,
  LIVE_OFF,
  LIVE_POLITE,
  // Haspopup values
  HASPOPUP_DIALOG,
  HASPOPUP_FALSE,
  HASPOPUP_GRID,
  HASPOPUP_LISTBOX,
  HASPOPUP_MENU,
  HASPOPUP_TREE,
  HASPOPUP_TRUE,
  // Current values
  CURRENT_DATE,
  CURRENT_FALSE,
  CURRENT_LOCATION,
  CURRENT_PAGE,
  CURRENT_STEP,
  CURRENT_TIME,
  CURRENT_TRUE,
  // Autocomplete values
  AUTOCOMPLETE_BOTH,
  AUTOCOMPLETE_INLINE,
  AUTOCOMPLETE_LIST,
  AUTOCOMPLETE_NONE,
  // Orientation values
  ORIENTATION_HORIZONTAL,
  ORIENTATION_VERTICAL,
  // Sort values
  SORT_ASCENDING,
  SORT_DESCENDING,
  SORT_NONE,
  SORT_OTHER,
  // Options arrays
  ARIA_AUTOCOMPLETE_OPTIONS,
  ARIA_CURRENT_OPTIONS,
  ARIA_HASPOPUP_OPTIONS,
  ARIA_LIVE_OPTIONS,
  ARIA_ORIENTATION_OPTIONS,
  ARIA_ROLE_OPTIONS,
  ARIA_SORT_OPTIONS,
};
